import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, Clock, ShoppingBag, Tag } from "lucide-react";
import {
  Badge,
  cn,
  ServicePresentationCard,
  serviceItemImageObjectPositionClass,
} from "@cliently/ui";
import { resolveOrganizationMediaUrl } from "../../lib/organization-media-url";
import { formatMoneyEur } from "../../lib/format-money";
import type { PortalServiceItem } from "../../types/portal-service-item";
import { BookingServiceDescriptionPreview } from "./BookingServiceItemCard";

/** Largeur max des cartes présentation dans la grille (Services, À propos). */
export const BOOKING_PRESENTATION_CARD_MAX_WIDTH_PX = 300;
/** Hauteur uniforme des cartes (Services, À propos) — avec ou sans description. */
export const BOOKING_PRESENTATION_CARD_MAX_HEIGHT_PX = 375;
/** Bandeau image (complète le total avec titre + zone texte + pied). */
export const BOOKING_PRESENTATION_CARD_MEDIA_HEIGHT_PX = 168;
/** Aperçu description repliée : plus haut que la liste RDV pour retarder « Voir plus ». */
export const BOOKING_PRESENTATION_DESCRIPTION_PREVIEW_H_PX = 112;
const CARD_MEDIA_HEIGHT_CLASS = "h-[168px]";

export interface BookingServicePresentationCardProps {
  service: PortalServiceItem;
  onBook: () => void;
  bookLabel: string;
  /** Libellé court pour le bouton (mobile). Défaut : « Réserver ». — utilisé pour aria-label si besoin. */
  bookLabelShort?: string;
  className?: string;
}

/**
 * Carte de présentation d'une prestation (Services, À propos, …) :
 * toute la surface est cliquable pour réserver (sauf « Voir plus / moins » de la description).
 */
export const BookingServicePresentationCard = ({
  service,
  onBook,
  bookLabel,
  bookLabelShort,
  className,
}: Readonly<BookingServicePresentationCardProps>) => {
  const { t, i18n } = useTranslation();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  const serviceImage = service.image
    ? (resolveOrganizationMediaUrl(service.image) ?? service.image)
    : null;

  const description = service.description;
  const hasDescription =
    !!description &&
    typeof description === "object" &&
    Object.keys(description).length > 0;

  const ariaLabel =
    bookLabelShort == null ? bookLabel : `${bookLabel} (${bookLabelShort})`;

  const promo = service.activePromotion;
  const hasPromo = !!promo && promo.percentOff > 0;
  const promoPrice = hasPromo
    ? service.price * (1 - promo.percentOff / 100)
    : service.price;

  return (
    <ServicePresentationCard
      variant="default"
      className={className}
      mediaClassName={CARD_MEDIA_HEIGHT_CLASS}
      label={service.label}
      maxWidthPx={BOOKING_PRESENTATION_CARD_MAX_WIDTH_PX}
      onClick={onBook}
      ariaLabel={ariaLabel}
      media={
        serviceImage ? (
          <img
            src={serviceImage}
            alt={service.label}
            className={cn(
              "h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]",
              serviceItemImageObjectPositionClass(
                service.imageObjectPosition,
              ),
            )}
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1">
            <ShoppingBag
              className="text-primary/40 h-12 w-12 sm:h-14 sm:w-14"
              aria-hidden
            />
          </div>
        )
      }
      mediaOverlayTop={
        <div className="flex w-full items-start justify-between">
          {hasPromo ? (
            <Badge className="bg-emerald-600 text-white shadow-sm text-[11px] gap-1 px-2 py-0.5">
              <Tag className="h-3 w-3" />
              -{promo.percentOff}%
            </Badge>
          ) : (
            <span />
          )}
          <span
            className="bg-background/90 text-primary ring-border/60 flex h-8 w-8 items-center justify-center rounded-lg shadow-sm ring-1 backdrop-blur-sm"
            aria-hidden
          >
            <Calendar className="h-4 w-4" />
          </span>
        </div>
      }
      description={
        hasDescription && description ? (
          <div
            className={cn(
              "min-h-0",
              descriptionExpanded
                ? "h-full overflow-y-auto overscroll-contain"
                : "h-full overflow-hidden",
            )}
          >
            <div className="min-w-0">
              <BookingServiceDescriptionPreview
                content={description}
                compact={!descriptionExpanded}
                compactPreviewHeightPx={
                  BOOKING_PRESENTATION_DESCRIPTION_PREVIEW_H_PX
                }
                onExpandChange={setDescriptionExpanded}
              />
            </div>
          </div>
        ) : null
      }
      footerLeft={
        <span className="text-muted-foreground inline-flex items-center gap-1 text-xs tabular-nums">
          <Clock className="h-3.5 w-3.5 shrink-0 opacity-80" />
          {service.duration} min
        </span>
      }
      footerRight={
        hasPromo ? (
          <span className="flex flex-col items-end gap-0">
            <span className="text-muted-foreground text-[11px] line-through tabular-nums">
              {formatMoneyEur(service.price, i18n.language)}
            </span>
            <span className="text-emerald-600 text-[15px] font-bold tabular-nums sm:text-base">
              {formatMoneyEur(promoPrice, i18n.language)}
            </span>
          </span>
        ) : (
          <span className="text-primary text-[15px] font-bold tabular-nums sm:text-base">
            {service.price === 0
              ? t("booking.priceFree", "Gratuit")
              : formatMoneyEur(service.price, i18n.language)}
          </span>
        )
      }
    />
  );
};
